import React, { useCallback } from 'react'
import { useDebouncedState } from 'hooks/useDebouncedState'
import useReportPageUrlState from 'hooks/useReportPageUrlState'
import { Link } from 'react-router-dom'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import { styled } from '@mui/material/styles'
import LinearProgress from '@mui/material/LinearProgress'
import InputBase from '@mui/material/InputBase'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import List from '@mui/material/List'
import ListSubheader from '@mui/material/ListSubheader'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Box from '@mui/material/Box'
import Grow from '@mui/material/Grow'
import {
  useSearchReportsQuery,
  useSearchSectionsQuery,
  SearchHit,
} from 'gqlTypes'

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.common.white,
  color: theme.palette.text.primary,
  marginLeft: '128px',
  height: '100%',
}))

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}))

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  height: '100%',
  border: `1px solid ${theme.palette.primary.main}`,
  '& .MuiInputBase-input': {
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    width: '455px',
  },
}))

const Highlighted = ({ text = '', highlight = '' }) => {
  if (!highlight.trim()) {
    return <Typography>{text}</Typography>
  }
  const regex = new RegExp(`(${highlight})`, 'gi')
  const parts = text.split(regex)
  return (
    <Typography>
      {parts.filter(String).map((part, i) => {
        return regex.test(part) ? (
          <b key={i}>{part}</b>
        ) : (
          <span key={i}>{part}</span>
        )
      })}
    </Typography>
  )
}

const NavbarSearch: React.FC = () => {
  const { reportId } = useReportPageUrlState()
  const [query, setQuery, rawQuery] = useDebouncedState('')
  const reportsQuery = useSearchReportsQuery({
    variables: { query },
  })
  const sectionsQuery = useSearchSectionsQuery({
    variables: { query, reportId: reportId! },
    skip: !reportId,
  })
  const loading = reportsQuery.loading || sectionsQuery.loading
  const reports = reportsQuery.data?.searchReports ?? []
  const sections = sectionsQuery.data?.searchSections ?? []

  const clearSearchQuery = useCallback(() => {
    setQuery('')
  }, [])

  const renderListItems = useCallback(
    (items: SearchHit[]) => {
      if (!items.length) {
        return (
          <Box sx={{ ml: 2, mb: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
              No results
            </Typography>
          </Box>
        )
      }
      return items.map((res, idx) => (
        <ListItem key={idx} disablePadding>
          <ListItemButton
            component={Link}
            to={
              res.sectionId
                ? `/reports/${res.reportId}/sections/${res.sectionId}`
                : `/reports/${res.reportId}`
            }
            onClick={clearSearchQuery}
          >
            <ListItemText
              sx={{ ml: 3 }}
              primary={<Highlighted text={res.title} highlight={rawQuery} />}
            />
          </ListItemButton>
        </ListItem>
      ))
    },
    [clearSearchQuery, rawQuery]
  )

  return (
    <ClickAwayListener onClickAway={clearSearchQuery}>
      <Search id="searchbar">
        <SearchIconWrapper>
          <SearchIcon />
        </SearchIconWrapper>
        <StyledInputBase
          placeholder="Search…"
          inputProps={{ 'aria-label': 'search' }}
          value={rawQuery}
          onChange={(e) => {
            setQuery(e.target.value)
          }}
        />
        {!!rawQuery.length && (
          <IconButton
            sx={{ position: 'absolute', right: 0, height: '100%', mr: 1 }}
            onClick={clearSearchQuery}
          >
            <ClearIcon />
          </IconButton>
        )}
        <Grow in={!!rawQuery.length} style={{ transformOrigin: 'top center' }}>
          <Paper
            elevation={3}
            sx={{
              position: 'absolute',
              width: 400,
              height: 400,
              overflowY: 'scroll',
              zIndex: (theme) => theme.zIndex.appBar,
            }}
          >
            {loading ? (
              <LinearProgress />
            ) : (
              <>
                {reportId && (
                  <List subheader={<ListSubheader>This Report</ListSubheader>}>
                    {renderListItems(sections)}
                    <Divider />
                  </List>
                )}
                <List subheader={<ListSubheader>All Reports</ListSubheader>}>
                  {renderListItems(reports)}
                </List>
              </>
            )}
          </Paper>
        </Grow>
      </Search>
    </ClickAwayListener>
  )
}

export default NavbarSearch
